<template>
  <div class="contract-tracker" data-private>
    <!-- ============ HEADER BAND: identity + overall score ============ -->
    <div class="px-5 py-4 border-b border-gray-100">
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded"
                  :class="methodChip">{{ methodologyName }}</span>
            <span v-if="spec.payer_name"
                  class="text-[10px] font-medium text-gray-500">{{ spec.payer_name }}</span>
            <span v-if="spec.performance_year"
                  class="text-[10px] px-1.5 py-0.5 rounded"
                  :class="spec.year_is_fallback ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-500'">
              PY {{ spec.performance_year }}<span v-if="spec.year_is_fallback"> · latest available</span>
            </span>
          </div>
          <h3 class="text-sm font-semibold text-gray-900 mt-1 truncate">
            {{ contractName }}
          </h3>
          <div class="text-[10px] text-gray-400 mt-0.5">Contract ID: {{ spec.contract_id }}</div>
          <div v-if="metSummaryText" class="text-[10px] text-gray-400 mt-0.5">{{ metSummaryText }}</div>
          <p v-if="!spec.valid" class="flex items-start gap-1 text-[11px] text-red-600 mt-1.5">
            <font-awesome-icon :icon="['fas', 'triangle-exclamation']" class="mt-0.5 shrink-0" />
            <span>{{ spec.validation_errors.length }} config item{{ spec.validation_errors.length > 1 ? 's' : '' }}
              to QA: {{ spec.validation_errors.slice(0, 2).join('; ') }}<span
              v-if="spec.validation_errors.length > 2"> …</span></span>
          </p>
        </div>
        <div class="text-right shrink-0">
          <div class="text-[10px] text-gray-500 uppercase tracking-wide">Overall</div>
          <div class="text-2xl font-bold leading-tight" :class="scoreColor">{{ headlineScore }}</div>
          <div v-if="result && result.projected_label" class="text-[11px] text-[#4573D2] mt-0.5">
            <font-awesome-icon :icon="['fas', 'arrow-trend-up']" class="text-[9px]" />
            {{ result.projected_label }} projected
          </div>
          <!-- Year-end trend. Distinct from "projected" above: that is credit
               for work already done, this is where the contract lands in
               December if the current trajectory holds. Same tangerine as the
               chart's projected line so the two read as one idea. -->
          <div v-if="result && result.trend_label"
               class="text-[11px] text-[#9F4D30] mt-0.5"
               :title="`If the current month-over-month trajectory continues. `
                 + `Based on ${result.trend_measure_count} measure(s) with enough history.`">
            <font-awesome-icon :icon="['fas', 'chart-line']" class="text-[9px]" />
            {{ result.trend_label }} at year end
          </div>
        </div>
      </div>

      <!-- savings progress toward threshold -->
      <div v-if="result && result.max_score" class="mt-3">
        <div class="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
          <div class="h-1.5 rounded-full transition-all"
               :class="result.savings_eligible ? 'bg-green-600' : 'bg-primary-500'"
               :style="{ width: overallFill + '%' }"></div>
        </div>
        <div class="flex justify-between text-[10px] text-gray-400 mt-1">
          <span>{{ formatScore(result.score) }} / {{ formatScore(result.max_score) }}{{ unitSuffix }}</span>
          <span v-if="result.savings_eligible" class="text-green-700 font-medium">
            <font-awesome-icon :icon="['fas', 'check']" class="text-[9px]" /> savings eligible
          </span>
          <span v-else-if="result.gap_to_savings">{{ formatScore(result.gap_to_savings) }} to savings</span>
        </div>
      </div>
    </div>

    <!-- ============ BODY ============ -->
    <div class="p-5">
      <div v-if="!result || !result.measures || result.measures.length === 0"
           class="py-10 text-center text-gray-400 text-sm">
        No scored measures for this contract.
      </div>

      <template v-else>
        <!-- PMPM dollar projection banner (only relevant for pmpm) -->
        <div v-if="methodology === 'min_max_pmpm' && result.projected_dollars != null"
             class="mb-3 text-xs text-green-700 font-medium">
          Projected reward ≈ ${{ Number(result.projected_dollars).toLocaleString() }}
        </div>

        <!-- ===== AGGREGATE PROGRESS OVER TIME =====
             Jen Fahringer 2026-07-30 confirmed this is the high-level roll-up
             ("that's just overall closure, correct?" -> numerator/denominator
             across all the contract's tracked measures), and asked for it
             MONTHLY, not weekly ("we don't see measure change too much week
             over week"). The glide path shows where the contract should be to
             land on target by year end. -->
        <div class="mb-5 rounded border border-gray-200 bg-white">
          <div class="flex items-center justify-between px-3 pt-2.5">
            <div>
              <div class="text-[11px] font-semibold text-gray-700">Overall closure over time</div>
              <div class="text-[10px] text-gray-400">
                All tracked measures combined
                <span v-if="momDeltaLabel"> · {{ momDeltaLabel }}</span>
              </div>
            </div>
            <div class="flex items-center gap-1">
              <button v-for="g in ['Monthly', 'Weekly']" :key="g" type="button"
                      class="text-[10px] px-2 py-0.5 rounded border"
                      :class="trendGrain === g.toLowerCase()
                        ? 'bg-gray-800 text-white border-gray-800'
                        : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'"
                      @click="setTrendGrain(g.toLowerCase())">{{ g }}</button>
            </div>
          </div>
          <div v-if="historyLoading" class="py-10 text-center">
            <font-awesome-icon :icon="['fas', 'spinner']" spin class="text-gray-300" />
          </div>
          <div v-else-if="!trendPoints.length" class="py-8 text-center text-[11px] text-gray-400">
            No history for this contract yet.
          </div>
          <Chart_V5 v-else
                    :key="trendChartKey"
                    :chart_data="trendChartData"
                    :chart_config="trendChartConfig"
                    override-height="h-[220px]" />
        </div>

        <!-- UNIFIED, PERFORMANCE-FORWARD MEASURE LIST -->
        <div class="space-y-3.5">
          <div v-for="m in sortedMeasures" :key="m.measure_code"
               :class="m.dropped_low_denominator ? 'opacity-40' : ''">
            <!-- top line: measure name + performance number (hero) + score badge -->
            <div class="flex items-center justify-between gap-3 mb-1">
              <span class="text-xs font-medium text-gray-800 truncate">
                <!-- Drill-down. Danea 2026-07-30: "individually, I would go
                     automatically to practice level". Clicking the measure name
                     opens this contract's patients for that measure; the arrow
                     opens the SAME measure across ALL contracts ("I want to look
                     across all lines of all of our payers"). -->
                <button type="button"
                        class="text-left hover:text-primary-600 hover:underline"
                        :title="`Patients with an open ${m.measure_code} gap in this contract`"
                        @click="drillMeasure(m, false)">
                  {{ m.measure_code }} · {{ m.measure_name }}
                </button>
                <button type="button"
                        class="ml-1 text-[9px] text-gray-400 hover:text-primary-600"
                        :title="`${m.measure_code} across all contracts`"
                        @click="drillMeasure(m, true)">
                  <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" />
                </button>
                <span v-if="m.dropped_low_denominator"
                      class="text-[9px] text-gray-400 font-normal">· excluded (low denom)</span>
              </span>
              <div class="flex items-baseline gap-2 shrink-0">
                <span class="text-sm font-bold tabular-nums" :class="rateColor(m)">{{ m.rate }}%</span>
                <!-- Year-end trend rate, only when it moves the number. -->
                <span v-if="m.trend_rate != null"
                      class="text-[10px] text-[#9F4D30] tabular-nums whitespace-nowrap"
                      :title="'Trending to ' + m.trend_rate + '% by year end if the '
                        + 'current trajectory continues'">→ {{ m.trend_rate }}%</span>
                <span class="text-[11px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap"
                      :class="scoreBadgeClass(m)">{{ scoreBadge(m) }}</span>
                <!-- Only shown when the trend crosses into a HIGHER tier. A
                     trend that gains rate without gaining a star is already
                     visible in the rate above; repeating it here would imply a
                     score change that has not happened. -->
                <span v-if="trendGainsTier(m)"
                      class="text-[10px] font-medium text-[#9F4D30] whitespace-nowrap"
                      :title="'On the current trajectory this measure reaches '
                        + m.trend_label + ' by year end'">→ {{ m.trend_label }}</span>
                <span v-if="achievedDisplay(m)"
                      class="text-[10px] text-gray-400 whitespace-nowrap">{{ achievedDisplay(m) }}</span>
              </div>
            </div>

            <!-- performance bar: fill = current rate, hatched = year-end trend,
                 tick = goal/target -->
            <div class="relative h-2.5 bg-gray-100 rounded-full overflow-visible">
              <!-- Trend segment sits UNDER the actual fill and spans the whole
                   trended width, so the actual bar paints over its left end and
                   only the projected remainder shows. Tangerine hatch, matching
                   the chart's projected line — never a solid fill, which would
                   read as achieved performance. -->
              <div v-if="m.trend_rate != null"
                   class="absolute inset-y-0 left-0 rounded-full"
                   :style="{
                     width: clampPct(m.trend_rate) + '%',
                     backgroundImage: 'repeating-linear-gradient(45deg,'
                       + '#9F4D30 0 3px, transparent 3px 6px)',
                     opacity: 0.55,
                   }"
                   :title="`Trending to ${m.trend_rate}% by year end`"></div>
              <div class="absolute inset-y-0 left-0 rounded-full transition-all"
                   :class="barColor(m)" :style="{ width: clampPct(m.rate) + '%' }"></div>
              <!-- goal marker -->
              <div v-if="goalPct(m) != null"
                   class="absolute -top-1 h-4 w-px bg-gray-800"
                   :style="{ left: goalPct(m) + '%' }" :title="`goal ${goalPct(m)}%`"></div>
            </div>

            <!-- bottom line: counts + goal + gap -->
            <div class="flex items-center justify-between text-[10px] text-gray-400 mt-1">
              <span>{{ m.numerator }} / {{ m.denominator }} closed</span>
              <span class="flex items-center gap-2">
                <span v-if="goalPct(m) != null">goal {{ goalPct(m) }}%</span>
                <span v-if="m.gaps_to_next" class="text-primary-600 font-medium">
                  {{ m.gaps_to_next }} to {{ m.next_tier_label || 'goal' }}
                </span>
                <!-- Met status. Single indicator: at_ceiling and met===true mean
                     the same thing to a reader, so never render both. -->
                <span v-else-if="m.met === true || m.at_ceiling" class="text-green-600 font-medium">
                  <font-awesome-icon :icon="['fas', 'check']" class="text-[9px]" /> Met
                </span>
                <span v-else-if="m.met === false && m.projected_met === true"
                      class="text-[#4573D2] font-medium"
                      title="Not yet credited by the payer, but the quality team's completed work would close it">
                  <font-awesome-icon :icon="['fas', 'arrow-trend-up']" class="text-[9px]" /> Met (projected)
                </span>
                <span v-else-if="m.met === false" class="text-gray-400">Not met</span>
              </span>
            </div>

            <!-- benchmark chips (Cigna only) -->
            <div v-if="methodology === 'benchmark_any' && m.benchmarks && m.benchmarks.length"
                 class="flex flex-wrap gap-1 mt-1.5">
              <span v-for="(b, i) in m.benchmarks" :key="i"
                    class="text-[9px] px-1.5 py-0.5 rounded border inline-flex items-center gap-1"
                    :class="benchmarkClass(b)">
                <span class="capitalize">{{ b.label }}</span>{{ pct(b.min_rate) }}
                <font-awesome-icon v-if="b.is_lowest" :icon="['fas', 'star']" class="text-[7px]" />
              </span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- ============ FOOTER: data provenance ============ -->
    <div v-if="spec.last_report_date"
         class="px-5 py-2.5 border-t border-gray-100 bg-gray-50
                flex items-center justify-between text-[10px] text-gray-400">
      <span>
        <font-awesome-icon :icon="['fas', 'file-lines']" class="mr-1" />
        Last report parsed: <span class="font-medium text-gray-500">{{ spec.last_report_date }}</span>
      </span>
      <span v-if="spec.last_report_source" class="truncate max-w-[55%] text-right">{{ spec.last_report_source }}</span>
    </div>
  </div>
</template>

<script>
/**
 * Contract_Tracker — the single, config-driven renderer.
 * Branches on spec.methodology to draw the contract in its own idiom.
 * The overall (rolled-up) score is the big number in the header band;
 * per-measure scores render in the body. Adding a methodology = one branch.
 */
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';

export default {
  name: 'Contract_Tracker',
  components: { Chart_V5 },
  props: {
    spec: { type: Object, required: true },
    result: { type: Object, default: null },
  },
  data() {
    return {
      historyLoading: false,
      history: [],          // weekly points as returned
      historyMonthly: [],   // month-end points
      glidePath: [],
      targetRate: null,
      projectedYearEndRate: null,
      momDelta: null,
      priorMonth: null,
      trendGrain: 'monthly',
      // Bumped when the series changes. Chart_V5 snapshots clientWidth in
      // mounted(), so it must remount once real data arrives or it renders at
      // zero width and appears blank.
      trendChartKey: 0,
    };
  },
  watch: {
    // The pane is reused as the user clicks between contracts, so refetch
    // whenever the CPY changes.
    'spec.cpy_entity_id': {
      immediate: true,
      handler(id) { if (id) this.loadHistory(id); },
    },
  },
  computed: {
    methodology() { return this.spec.methodology; },
    methodologyName() { return (this.spec.methodology_name || this.spec.methodology || '').replace(/\s*\(.*\)/, ''); },
    contractName() {
      return this.spec.contract_name
        || (this.spec.cpy_name || this.spec.contract_id || '').replace(/\s*20\d\d$/, '');
    },
    headlineScore() { return this.result ? this.result.label : '—'; },
    scoreColor() {
      if (!this.result) return 'text-gray-300';
      return this.result.savings_eligible ? 'text-green-700' : 'text-gray-900';
    },
    unitSuffix() {
      if (this.spec.unit === 'cents_pmpm') return '¢';
      if (this.spec.unit === 'stars') return '★';
      return '';
    },
    overallFill() {
      if (!this.result || !this.result.max_score) return 0;
      return Math.max(0, Math.min(100, (this.result.score / this.result.max_score) * 100));
    },
    methodChip() {
      const map = {
        ma_stars: 'bg-amber-50 text-amber-700',
        min_max_points: 'bg-primary-100 text-primary-700',
        min_max_pmpm: 'bg-green-50 text-green-700',
        benchmark_any: 'bg-neutral-100 text-neutral-800',
        medicaid_tiers: 'bg-violet-50 text-violet-700',
        single_goal: 'bg-gray-100 text-gray-600',
      };
      return map[this.methodology] || 'bg-gray-100 text-gray-600';
    },
    sortedMeasures() {
      // Performance-forward: biggest gap-to-goal first (draws the eye to the
      // measures that need work), excluded/low-denom measures last.
      const list = (this.result && this.result.measures) ? [...this.result.measures] : [];
      return list.sort((a, b) => {
        if (a.dropped_low_denominator !== b.dropped_low_denominator) {
          return a.dropped_low_denominator ? 1 : -1;
        }
        return this.gapToGoal(b) - this.gapToGoal(a);
      });
    },
    trendPoints() {
      return this.trendGrain === 'monthly' ? this.historyMonthly : this.history;
    },
    momDeltaLabel() {
      if (this.momDelta == null || !this.priorMonth) return '';
      const sign = this.momDelta > 0 ? '+' : '';
      return `${sign}${this.momDelta} pts vs ${this.priorMonth}`;
    },
    trendChartData() {
      const pts = this.trendPoints;
      if (!pts.length) return { labels: [], series: [] };
      const labels = pts.map((p) => {
        const raw = p.month || p.report_date;
        const dt = new Date(`${String(raw).slice(0, 10)}${p.month ? '-01' : ''}T00:00:00`);
        return Number.isNaN(dt.getTime())
          ? String(raw)
          : dt.toLocaleDateString('en-US',
            p.month ? { month: 'short', year: '2-digit' } : { month: 'short', day: 'numeric' });
      });
      // Actual and projected are separate series so the projection can render
      // dashed. The projected series repeats the last ACTUAL point so the two
      // lines join instead of leaving a gap at the handover month.
      const lastActualIdx = pts.reduce(
        (acc, p, i) => (p.is_actual !== false && p.rate != null ? i : acc), -1);
      const series = [{
        values: pts.map((p, i) => (
          p.rate != null && i <= lastActualIdx ? p.rate / 100 : null)),
        config: {
          label: 'Overall closure',
          backgroundColor: '#2563EB',
          type: 'line',
          yAxis: 'left',
          offset: 0,
          strokeWidth: 2.5,
        },
      }];
      if (pts.some((p) => p.is_projected)) {
        series.push({
          values: pts.map((p, i) => (
            (p.is_projected || i === lastActualIdx) && p.rate != null
              ? p.rate / 100 : null)),
          config: {
            // Tangerine against the actual series' blue. Actual and projected
            // are DIFFERENT KINDS OF NUMBER — one measured, one modelled — so
            // they must be told apart instantly, on a screen share, from across
            // a room. A second shade of blue does not do that.
            // Primary-700 rather than the brighter Primary-500: 5.87:1 on white
            // (500 is only 3.02:1) and 2.31:1 clear of the grey glide path
            // (500 is 1.19:1 — nearly the same line).
            label: 'Projected',
            backgroundColor: '#9F4D30',
            type: 'line',
            yAxis: 'left',
            offset: 0,
            strokeWidth: 2,
            dashed: true,
          },
        });
      }
      // Glide path is computed on the weekly spine; only overlay it there so the
      // two series stay aligned point-for-point.
      if (this.trendGrain === 'weekly' && this.glidePath.length === pts.length) {
        series.push({
          values: this.glidePath.map((g) => {
            const v = (g && (g.projected_rate ?? g.rate));
            return v != null ? v / 100 : null;
          }),
          config: {
            label: 'Glide path',
            backgroundColor: '#9CA3AF',
            type: 'line',
            yAxis: 'left',
            offset: 0,
            strokeWidth: 1.5,
            dashed: true,
          },
        });
      }
      return { labels, series };
    },
    trendChartConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 24, right: 40, bottom: 42, left: 52 },
        xAxis: { labelRotation: -45, fontSize: 9 },
        yAxes: [{ position: 'left', fontSize: 10 }],
        legend: { display: this.trendChartData.series.length > 1, position: 'top', spacing: 10 },
        labelDisplayConfig: { maxLabels: 18, minLabelSpacing: 4 },
        formatting: { percentage: true, sig_figs: 1 },
      };
    },
    // Summary of how many measures have met their goal (actual vs projected).
    metCounts() {
      const measures = (this.result && this.result.measures) || [];
      const eligible = measures.filter((m) => m.met !== null && m.met !== undefined);
      const total = eligible.length;
      const met = eligible.filter((m) => m.met === true).length;
      const projectedMet = eligible.filter((m) => m.met === true || m.projected_met === true).length;
      return { total, met, projectedMet };
    },
    metSummaryText() {
      const { total, met, projectedMet } = this.metCounts;
      if (!total) return '';
      let text = `${met} of ${total} measure${total === 1 ? '' : 's'} met`;
      if (projectedMet !== met) text += ` · ${projectedMet} projected`;
      return text;
    },
  },
  methods: {
    pct(r) { return r == null ? '' : `${(r * 100).toFixed(0)}%`; },
    formatScore(v) { return v == null ? '—' : (Math.round(v * 100) / 100); },
    clampPct(v) { return Math.max(0, Math.min(100, v || 0)); },
    // True only when the year-end trend crosses into a higher scoring tier than
    // the measure currently sits in. Compared against the PROJECTED score where
    // one exists, so completed-but-uncredited work doesn't get counted twice as
    // a "gain".
    trendGainsTier(m) {
      if (!m || m.trend_score == null || !m.trend_label) return false;
      const base = m.projected_score != null ? m.projected_score : m.score;
      return m.trend_score > base;
    },
    specMeasure(m) {
      return (this.spec.measures || []).find((x) => x.code === m.measure_code) || null;
    },
    tierCut(t, inverse) {
      return inverse ? (t.max_rate ?? t.min_rate) : (t.min_rate ?? t.max_rate);
    },
    tiersFor(m) {
      const sm = this.specMeasure(m);
      const tiers = (sm && sm.tiers) || [];
      const inverse = !!(sm && sm.inverse);
      return [...tiers].sort((a, b) => (this.tierCut(a, inverse) || 0) - (this.tierCut(b, inverse) || 0));
    },
    // The goal line = the best tier threshold for this measure.
    goalRate(m) {
      const tiers = this.tiersFor(m);
      if (!tiers.length) return null;
      const sm = this.specMeasure(m);
      const inverse = !!(sm && sm.inverse);
      const vals = tiers.map((t) => this.tierCut(t, inverse)).filter((v) => v != null);
      if (!vals.length) return null;
      return inverse ? Math.min(...vals) : Math.max(...vals);
    },
    goalPct(m) {
      const g = this.goalRate(m);
      return g == null ? null : Math.round(g * 100);
    },
    gapToGoal(m) {
      const g = this.goalPct(m);
      if (g == null) return 0;
      return Math.max(0, g - (m.rate || 0));
    },
    // rate color: green if at/above goal, primary if close, gray otherwise
    rateColor(m) {
      const g = this.goalPct(m);
      if (g == null) return 'text-gray-800';
      if (m.rate >= g) return 'text-green-700';
      if (m.rate >= g - 10) return 'text-gray-800';
      return 'text-gray-800';
    },
    barColor(m) {
      const g = this.goalPct(m);
      if (g != null && m.rate >= g) return 'bg-green-600';
      if (this.methodology === 'min_max_pmpm') return 'bg-green-600';
      return 'bg-primary-500';
    },
    // compact score badge on the right, adapts to methodology
    scoreBadge(m) {
      if (this.methodology === 'ma_stars') return m.label;
      if (this.methodology === 'min_max_pmpm') return `${m.cents_per_member}¢`;
      if (this.methodology === 'single_goal') return m.label;
      if (this.methodology === 'benchmark_any') return m.score > 0 ? 'point' : 'no point';
      return `${m.score}/${m.max_score} pt`;
    },
    scoreBadgeClass(m) {
      const met = (this.methodology === 'single_goal' || this.methodology === 'benchmark_any')
        ? m.score > 0 : (this.goalPct(m) != null && m.rate >= this.goalPct(m)) || m.at_ceiling;
      return met ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500';
    },
    benchmarkClass(b) {
      if (b.met) return 'bg-green-50 text-green-700 border-green-200';
      return 'bg-gray-50 text-gray-400 border-gray-200';
    },
    setTrendGrain(grain) {
      if (this.trendGrain === grain) return;
      this.trendGrain = grain;
      this.trendChartKey += 1;
    },
    async loadHistory(cpyId) {
      this.historyLoading = true;
      try {
        const res = await this.$axios.get(
          `/api/stars-performance/contracts/${cpyId}/history/`);
        const d = res.data || {};
        this.history = d.history || [];
        this.historyMonthly = d.history_monthly || [];
        this.glidePath = d.glide_path || [];
        this.targetRate = d.target_rate ?? null;
        this.projectedYearEndRate = d.projected_year_end_rate ?? null;
        this.momDelta = d.mom_delta ?? null;
        this.priorMonth = d.prior_month ?? null;
        this.trendChartKey += 1;
      } catch (e) {
        this.history = [];
        this.historyMonthly = [];
        this.glidePath = [];
        this.projectedYearEndRate = null;
        this.momDelta = null;
        this.priorMonth = null;
      } finally {
        this.historyLoading = false;
      }
    },
    // Drill from a measure to its patient list.
    //   allContracts=false -> this contract only (practice/patient level)
    //   allContracts=true  -> the same measure across every contract
    // Quality_Patients_List already reads measure_codes/payer_name from the
    // route query, so this is a pure hand-off.
    drillMeasure(m, allContracts) {
      const query = { measure_codes: m.measure_code };
      if (!allContracts) {
        // The patient list filters on the contract NAME (it reads
        // opportunity metadata.contract_name), not the payer's contract id.
        const name = this.spec.contract_name || this.contractName;
        if (name) query.contract_name = name;
      }
      this.$router.push({ path: '/quality/patients', query });
    },
    // Achieved score display: prefer the human-readable label, fall back to
    // the raw score, render nothing when neither is present ("if applicable").
    achievedDisplay(m) {
      if (m.achieved_label) return m.achieved_label;
      if (m.achieved_score != null) return m.achieved_score;
      return '';
    },
  },
};
</script>
